/**
 * Shared form primitives for the per-workshop override section editors.
 *
 * Design: "Lit Operator's Manuscript". The one enforceable law is the
 * MANUSCRIPT INVERSION — if a string is UPPERCASE it must also be `font-mono`
 * AND be a label / status / index, never a value or a sentence. Mono is the
 * instrument chrome (eyebrows, indices, status, role tags, code-like values);
 * Inter (the default sans) carries every human value, label, and heading.
 * Controls are machined: a tinted input well with a crisp focus edge + soft
 * halo, an `aria-invalid` error path, and a lit green reactor-toggle.
 *
 * Plain inputs + CSS-var tokens, except `Select`: its open surface can't be
 * themed natively (the option list is OS chrome — a light popup in the middle
 * of the dark console), so it builds on the same Radix Select + `.log-pop`
 * panel chrome the Workshop Log controls use (`log-controls.tsx`). Prop
 * signatures are otherwise frozen — the section editors in `./sections/*`
 * and the renderer depend on them.
 */

import type { ReactNode } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp, Plus, RotateCcw, Save, Trash2 } from "lucide-react";

// Shared machined input well: Inter 14, 1px crisp edge + 3px halo on focus,
// red ring on aria-invalid, dimmed when disabled.
const INPUT_CLS =
  "w-full rounded-md border bg-[rgba(var(--accent-true-blue-rgb),0.05)] px-3 py-2 " +
  "text-fs-base leading-[1.4] text-[color:var(--text-primary)] outline-none " +
  "transition-[background-color,border-color,box-shadow] duration-150 ease-out " +
  "placeholder:text-[color:var(--text-muted)] " +
  "border-[rgba(var(--accent-true-blue-rgb),0.22)] " +
  "hover:border-[rgba(var(--accent-true-blue-rgb),0.40)] " +
  "focus:border-[rgba(var(--accent-true-blue-rgb),0.65)] " +
  "focus:shadow-[0_0_0_3px_rgba(var(--accent-true-blue-rgb),0.18)] " +
  "aria-[invalid=true]:border-[rgba(var(--accent-red-rgb),0.55)] " +
  "aria-[invalid=true]:focus:shadow-[0_0_0_3px_rgba(var(--accent-red-rgb),0.20)] " +
  "disabled:cursor-not-allowed disabled:opacity-40";

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-fs-micro font-medium leading-tight text-[color:var(--text-secondary)]">
        {label}
      </span>
      {children}
      {hint && (
        <span className="text-fs-label leading-relaxed text-[color:var(--text-muted)]">{hint}</span>
      )}
    </label>
  );
}

export function TextInput(
  props: React.InputHTMLAttributes<HTMLInputElement> & { mono?: boolean },
) {
  const { className, mono, ...rest } = props;
  return (
    <input
      {...rest}
      className={`${INPUT_CLS} ${mono ? "font-mono tracking-tight" : ""} ${className ?? ""}`}
    />
  );
}

export function TextArea(
  props: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { mono?: boolean },
) {
  const { className, mono, ...rest } = props;
  return (
    <textarea
      {...rest}
      className={`${INPUT_CLS} console-scroll min-h-[76px] resize-y leading-relaxed ${mono ? "font-mono" : ""} ${className ?? ""}`}
    />
  );
}

export interface SelectOption {
  value: string;
  label: string;
  /** Optional colour dot rendered before the label in the open list —
   *  the crew colour picker shows its accents in place. */
  swatch?: string;
}

/**
 * Console-chrome dropdown. The closed trigger sits in the machined-well
 * vocabulary (same `INPUT_CLS` as the text inputs) with an instrument cap:
 * hairline divider + chevron that rotates while open. The open list portals
 * into the shared `.log-pop` panel chrome (index.css) — same base as the
 * Workshop Log dropdowns — topped with a mono eyebrow readout and 2-digit
 * mono indices per row, echoing the modal's own "SECTION 06 / 09" language.
 * Radix keeps the native keyboard behaviour (type-ahead, arrows, Escape).
 */
export function Select({
  value,
  onChange,
  options,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  disabled?: boolean;
}) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    <SelectPrimitive.Root value={value} onValueChange={onChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        className={`${INPUT_CLS} group flex cursor-pointer items-center justify-between gap-2.5 text-left data-[state=open]:border-[rgba(var(--accent-true-blue-rgb),0.65)] data-[state=open]:shadow-[0_0_0_3px_rgba(var(--accent-true-blue-rgb),0.18)]`}
      >
        <span className="min-w-0 flex-1 truncate">
          <SelectPrimitive.Value />
        </span>
        <span
          aria-hidden
          className="h-[16px] w-px flex-shrink-0 bg-[rgba(var(--accent-true-blue-rgb),0.25)]"
        />
        <SelectPrimitive.Icon asChild>
          <ChevronDown
            aria-hidden
            className="h-3.5 w-3.5 flex-shrink-0 text-[color:var(--text-muted)] transition-transform duration-150 ease-out group-data-[state=open]:rotate-180 group-data-[state=open]:text-[color:var(--accent-sky)]"
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className="log-pop max-h-[min(300px,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)]"
        >
          {/* Instrument readout: which option is engaged, out of how many. */}
          <div
            aria-hidden
            className="mx-1 mb-1 flex items-center justify-between gap-3 border-b border-[rgba(var(--accent-true-blue-rgb),0.18)] px-1.5 pt-0.5 pb-1.5 font-mono text-fs-micro font-semibold tracking-[0.22em] text-[color:var(--text-muted)] uppercase"
          >
            <span className="inline-flex items-center gap-1.5">
              <span
                className="h-[5px] w-[5px] rounded-full"
                style={{
                  background: "var(--accent-sky)",
                  boxShadow: "0 0 5px rgba(var(--accent-sky-rgb),0.7)",
                }}
              />
              Select
            </span>
            <span style={{ color: "var(--accent-sky)" }}>
              {pad(Math.max(1, options.findIndex((o) => o.value === value) + 1))} /{" "}
              {pad(options.length)}
            </span>
          </div>
          {/* Chevron strips render only while options are hidden in that
              direction — with the themed viewport scrollbar (index.css)
              they make overflow legible at a glance. */}
          <SelectPrimitive.ScrollUpButton className="flex h-5 flex-shrink-0 cursor-default items-center justify-center text-[color:var(--accent-sky)]">
            <ChevronUp aria-hidden className="h-3 w-3" />
          </SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.Viewport>
            {options.map((o, i) => (
              <SelectPrimitive.Item
                key={o.value}
                value={o.value}
                className="flex cursor-pointer select-none items-center gap-2 rounded-md py-1.5 pl-2 pr-2 text-fs-label leading-[1.4] text-[color:var(--text-primary)] outline-none transition-colors duration-100 data-[highlighted]:bg-[rgba(var(--accent-true-blue-rgb),0.14)] data-[state=checked]:bg-[rgba(var(--accent-true-blue-rgb),0.06)]"
              >
                <span
                  aria-hidden
                  className="w-5 flex-shrink-0 text-right font-mono text-fs-micro font-medium tracking-[0.08em] text-[color:var(--text-muted)]"
                >
                  {pad(i + 1)}
                </span>
                {o.swatch && (
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                    style={{ background: o.swatch, boxShadow: `0 0 5px ${o.swatch}` }}
                  />
                )}
                <span className="min-w-0 flex-1 truncate">
                  <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
                </span>
                <SelectPrimitive.ItemIndicator asChild>
                  <Check
                    aria-hidden
                    strokeWidth={2.5}
                    className="h-3.5 w-3.5 flex-shrink-0 text-[color:var(--accent-sky)]"
                  />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton className="flex h-5 flex-shrink-0 cursor-default items-center justify-center text-[color:var(--accent-sky)]">
            <ChevronDown aria-hidden className="h-3 w-3" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

/**
 * The signature lit reactor-toggle (our styled checkbox). The check glyph is a
 * PEER SIBLING of the input — both inside the relative wrapper — so the
 * `peer-checked:` variants actually fire (a nested glyph would never light).
 * Keyboard-accessible: the real input sits transparent on top.
 */
export function ReactorToggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <span className="relative inline-flex h-4 w-4 flex-shrink-0 items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="peer absolute inset-0 z-10 cursor-pointer opacity-0 disabled:cursor-not-allowed"
      />
      <span
        aria-hidden
        className="h-4 w-4 rounded-[5px] border bg-[color:var(--bg-card)] transition-all duration-150 ease-out border-[color:hsl(var(--border))] peer-hover:border-[rgba(var(--accent-true-blue-rgb),0.55)] peer-focus-visible:[box-shadow:0_0_0_2px_rgba(var(--accent-true-blue-rgb),0.55)] peer-active:scale-90 peer-checked:border-[color:var(--accent-green)] peer-checked:bg-[color:var(--accent-green)] peer-checked:[box-shadow:var(--glow-green)] peer-disabled:opacity-30"
      />
      <Check
        aria-hidden
        strokeWidth={3}
        style={{ color: "var(--bg-panel)" }}
        className="pointer-events-none absolute h-3 w-3 scale-50 opacity-0 transition-all duration-[175ms] ease-out peer-checked:scale-100 peer-checked:opacity-100"
      />
    </span>
  );
}

/** A bordered card wrapping one item in an editable list, with optional
 *  move-up/down + remove controls in the top-right. */
export function ListCard({
  title,
  onMoveUp,
  onMoveDown,
  onRemove,
  children,
}: {
  title?: string;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onRemove?: () => void;
  children: ReactNode;
}) {
  return (
    <div className="relative rounded-lg border border-[color:hsl(var(--border))] bg-[color:var(--bg-card)] p-4 transition-colors duration-150 hover:bg-[color:var(--bg-card-hover)]">
      <div className="absolute right-2.5 top-2.5 flex items-center gap-0.5">
        {onMoveUp && (
          <IconBtn label="Move up" onClick={onMoveUp}>
            <ChevronUp className="h-3.5 w-3.5" aria-hidden />
          </IconBtn>
        )}
        {onMoveDown && (
          <IconBtn label="Move down" onClick={onMoveDown}>
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          </IconBtn>
        )}
        {onRemove && (
          <IconBtn label="Remove" onClick={onRemove} danger>
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </IconBtn>
        )}
      </div>
      {title && (
        <div className="mb-3 text-fs-label font-semibold tracking-tight text-[color:var(--text-primary)]">
          {title}
        </div>
      )}
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

export function IconBtn({
  label,
  onClick,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`rounded-md p-1.5 text-[color:var(--text-muted)] outline-none transition-colors duration-150 active:translate-y-px focus-visible:[box-shadow:0_0_0_2px_rgba(var(--accent-sky-rgb),0.5)] ${
        danger
          ? "hover:bg-[rgba(var(--accent-red-rgb),0.12)] hover:text-[color:var(--accent-red)]"
          : "hover:bg-[rgba(var(--accent-true-blue-rgb),0.10)] hover:text-[color:var(--accent-true-blue)]"
      }`}
    >
      {children}
    </button>
  );
}

export function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 self-start rounded-md border border-dashed border-[rgba(var(--accent-true-blue-rgb),0.35)] px-3 py-2 text-fs-micro font-medium text-[color:var(--accent-true-blue)] outline-none transition-colors duration-150 hover:bg-[rgba(var(--accent-true-blue-rgb),0.08)] focus-visible:[box-shadow:0_0_0_2px_rgba(var(--accent-sky-rgb),0.4)]"
    >
      <Plus className="h-3.5 w-3.5" aria-hidden />
      {label}
    </button>
  );
}

/**
 * The frame every section renders inside: a scrolling body (console-scroll
 * themed bar; the dialog's own 920px cap keeps inputs from sprawling) plus a
 * sticky command bar. The footer carries a two-state readout derived only
 * from the existing props — sky "Unsaved changes" while `dirty`, gold
 * "Customised, saved" when an override is set and clean — then "Reset to
 * default" + the primary "Save".
 */
export function SectionFrame({
  description,
  overridden,
  dirty,
  onSave,
  onReset,
  children,
}: {
  description: string;
  overridden: boolean;
  dirty: boolean;
  onSave: () => void;
  onReset: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-shrink-0 items-start justify-between gap-4 pb-4">
        <p className="max-w-[60ch] text-fs-label leading-relaxed text-[color:var(--text-secondary)]">
          {description}
        </p>
        {overridden && (
          <span
            className="flex-shrink-0 rounded-md border px-2 py-0.5 font-mono text-fs-micro font-semibold tracking-[0.2em] uppercase"
            style={{
              color: "var(--accent-gold)",
              borderColor: "rgba(var(--accent-gold-rgb),0.5)",
              background: "rgba(var(--accent-gold-rgb),0.12)",
            }}
          >
            Customised
          </span>
        )}
      </div>

      <div className="console-scroll flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-2">
        {children}
      </div>

      <div
        className="mt-4 flex flex-shrink-0 items-center justify-between gap-3 pt-4"
        style={{ borderTop: "1px solid hsl(var(--border))" }}
      >
        <span className="flex items-center gap-2 font-mono text-fs-micro font-semibold tracking-[0.2em] uppercase">
          {dirty ? (
            <>
              <i
                className="h-[5px] w-[5px] rounded-full"
                style={{ background: "var(--accent-sky)", boxShadow: "0 0 6px rgba(var(--accent-sky-rgb),0.7)" }}
              />
              <span style={{ color: "var(--accent-sky)" }}>Unsaved changes</span>
            </>
          ) : overridden ? (
            <>
              <i
                className="h-[5px] w-[5px] rounded-full"
                style={{ background: "var(--accent-gold)", boxShadow: "0 0 6px rgba(var(--accent-gold-rgb),0.7)" }}
              />
              <span style={{ color: "var(--accent-gold)" }}>Customised, saved</span>
            </>
          ) : null}
        </span>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={!overridden}
            className="flex items-center gap-1.5 rounded-md border border-[color:hsl(var(--border))] px-3.5 py-2 text-fs-micro font-medium text-[color:var(--text-secondary)] outline-none transition-colors duration-150 active:translate-y-px hover:border-[rgba(var(--accent-true-blue-rgb),0.40)] hover:text-[color:var(--text-primary)] focus-visible:[box-shadow:0_0_0_2px_rgba(var(--accent-sky-rgb),0.4)] disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-[color:hsl(var(--border))] disabled:hover:text-[color:var(--text-secondary)]"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            Reset to default
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!dirty}
            className={`flex items-center gap-1.5 rounded-md border border-[rgba(var(--accent-true-blue-rgb),0.55)] bg-[rgba(var(--accent-true-blue-rgb),0.16)] px-4 py-2 text-fs-micro font-semibold text-[color:var(--accent-sky)] outline-none transition-[background-color,border-color,box-shadow] duration-150 active:translate-y-px hover:bg-[rgba(var(--accent-true-blue-rgb),0.24)] focus-visible:[box-shadow:0_0_0_3px_rgba(var(--accent-sky-rgb),0.4)] disabled:cursor-not-allowed disabled:opacity-35 ${
              dirty ? "[box-shadow:0_0_0_3px_rgba(var(--accent-true-blue-rgb),0.18)]" : ""
            }`}
          >
            <Save className="h-3.5 w-3.5" aria-hidden />
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
